import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { 
  Sparkles, 
  TrendingUp, 
  AlertTriangle,
  CheckCircle,
  Lightbulb,
  Brain
} from 'lucide-react';
import api from '../services/api';

const AIInsightsPage = () => {
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadInsights();
  }, []);

  const loadInsights = async () => {
    try {
      setLoading(true);
      const data = await api.getAIInsights();
      setInsights(data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading insights:', error);
      setLoading(false);
    }
  };

  const getInsightIcon = (type) => {
    switch (type) {
      case 'success': return CheckCircle;
      case 'trend': return TrendingUp;
      case 'alert': return AlertTriangle;
      default: return Lightbulb;
    }
  };

  const getInsightColor = (type) => {
    switch (type) {
      case 'success': return {
        bg: 'from-green-50 to-emerald-50',
        border: 'border-green-200',
        icon: 'bg-green-500',
        badge: 'bg-green-100 text-green-800 border-green-200'
      };
      case 'trend': return {
        bg: 'from-blue-50 to-cyan-50',
        border: 'border-blue-200',
        icon: 'bg-blue-500',
        badge: 'bg-blue-100 text-blue-800 border-blue-200'
      };
      case 'alert': return {
        bg: 'from-orange-50 to-yellow-50',
        border: 'border-orange-200',
        icon: 'bg-orange-500',
        badge: 'bg-orange-100 text-orange-800 border-orange-200'
      };
      default: return {
        bg: 'from-purple-50 to-pink-50',
        border: 'border-purple-200',
        icon: 'bg-purple-500',
        badge: 'bg-purple-100 text-purple-800 border-purple-200'
      };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-lg text-gray-600">Loading AI insights...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <Brain className="w-8 h-8 text-cyan-600" />
          <h2 className="text-3xl font-bold text-gray-900">AI Insights</h2>
        </div>
        <p className="text-gray-600">Intelligent recommendations and pattern analysis</p>
      </div>

      {/* AI Performance Overview */}
      <Card className="border-gray-200 bg-gradient-to-r from-cyan-50 to-blue-50">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-lg flex items-center justify-center">
              <Sparkles className="w-7 h-7 text-white" />
            </div>
            <div>
              <CardTitle className="text-2xl">T2 AI Performance</CardTitle>
              <CardDescription className="text-base">Real-time machine learning analysis</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-6">
            <div className="bg-white rounded-lg p-4 border border-cyan-200">
              <div className="text-sm text-gray-600 mb-1">Resolution Accuracy</div>
              <div className="text-3xl font-bold text-cyan-600">98.5%</div>
              <div className="text-xs text-gray-500 mt-1">↑ 2.3% from last week</div>
            </div>
            <div className="bg-white rounded-lg p-4 border border-cyan-200">
              <div className="text-sm text-gray-600 mb-1">Learning Rate</div>
              <div className="text-3xl font-bold text-blue-600">Fast</div>
              <div className="text-xs text-gray-500 mt-1">Improving daily</div>
            </div>
            <div className="bg-white rounded-lg p-4 border border-cyan-200">
              <div className="text-sm text-gray-600 mb-1">Confidence Score</div>
              <div className="text-3xl font-bold text-purple-600">92%</div>
              <div className="text-xs text-gray-500 mt-1">Very high</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Insights Grid */}
      <div className="space-y-4">
        <h3 className="text-xl font-semibold text-gray-900">Latest Insights</h3>
        {insights.map((insight, idx) => {
          const Icon = getInsightIcon(insight.type);
          const colors = getInsightColor(insight.type);
          
          return (
            <Card key={idx} className={`border-2 ${colors.border} bg-gradient-to-r ${colors.bg}`}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 ${colors.icon} rounded-lg flex items-center justify-center flex-shrink-0`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={colors.badge}>
                          {insight.type.charAt(0).toUpperCase() + insight.type.slice(1)}
                        </Badge>
                      </div>
                      <CardTitle className="text-xl mb-2">{insight.title}</CardTitle>
                      <CardDescription className="text-base text-gray-700">
                        {insight.description}
                      </CardDescription>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Lightbulb className="w-4 h-4 text-amber-500" />
                    <span className="text-sm font-semibold text-gray-900">AI Suggestion</span>
                  </div>
                  <p className="text-sm text-gray-700">{insight.suggestion}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Pattern Recognition */}
      <Card className="border-gray-200">
        <CardHeader>
          <CardTitle>Pattern Recognition</CardTitle>
          <CardDescription>AI-detected patterns in ticket data</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <div className="font-semibold text-gray-900 mb-1">Peak Hours Detected</div>
              <p className="text-sm text-gray-700">Most tickets submitted between 9AM-11AM. Consider pre-loading AI resources during these hours.</p>
            </div>
          </div>

          <div className="flex items-start gap-4 p-4 bg-green-50 rounded-lg border border-green-200">
            <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <CheckCircle className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <div className="font-semibold text-gray-900 mb-1">Auto-Resolution Success</div>
              <p className="text-sm text-gray-700">Password reset requests have 100% AI resolution rate. This category is fully automated.</p>
            </div>
          </div>

          <div className="flex items-start gap-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
            <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <Brain className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <div className="font-semibold text-gray-900 mb-1">Learning Adaptation</div>
              <p className="text-sm text-gray-700">AI has learned 15 new resolution patterns this week from your team's ticket handling.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recommendations */}
      <Card className="border-gray-200 bg-gradient-to-r from-amber-50 to-orange-50">
        <CardHeader>
          <div className="flex items-center gap-3">
            <Lightbulb className="w-6 h-6 text-amber-600" />
            <CardTitle>AI Recommendations</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-gray-700">
              Enable automated responses for "Software Installation" category - AI shows 89% confidence
            </p>
          </div>
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-gray-700">
              Create knowledge base article for recurring VPN issues - 12 similar tickets this month
            </p>
          </div>
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-gray-700">
              Schedule preventive maintenance for 3rd floor printer - pattern shows increasing failure rate
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AIInsightsPage;
